import React, { useState } from 'react';
import {
  Banner,
  Button,
  Gutter,
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Flashy,
  Checkbox,
  Textarea,
  Form,
  FormGroup,
  Layout,
  Radio,
  CondensedLarge,
  LayoutItem,
  Grid,
  GridRow,
  GridColumn,
  Well,
} from '@axiom/ui';
import type { Candidate, CandidateWorkFeedItem } from '@axiom/validation';
import { ExpressInterestSchema } from '@axiom/validation';

import { CandidateOpportunitiesApi } from '../../api/candidate-opportunities-api';
import { WorkFeedInterestUtil } from '../../utils/work-feed-interest-util';
import { WorkFeedEOIJobDescription } from '../WorkFeedEOIJobDescription/WorkFeedEOIJobDescription';

export type WorkFeedConfirmInterestModalProps = {
  candidate: Candidate;
  name?: string;
  onAfterSubmit: () => Promise<void>;
  onClose: () => void;
  workFeedItem: CandidateWorkFeedItem;
  submitButtonText?: string;
};

export const WorkFeedConfirmInterestModal = ({
  candidate,
  name = 'CONFIRM_INTEREST_OPPORTUNITY',
  onAfterSubmit,
  onClose,
  workFeedItem,
  submitButtonText = 'Submit',
}: WorkFeedConfirmInterestModalProps) => {
  const isPremium = workFeedItem.position.isPremium ?? false;

  const [
    showOpenToLowerEscCompensationPercentage,
    setShowOpenToLowerEscCompensationPercentage,
  ] = useState(false);

  const compensationDiscountOptions =
    WorkFeedInterestUtil.getCompensationDiscountOptions({
      isPremium,
      candidate,
    });

  return (
    <Form
      schema={ExpressInterestSchema}
      initialValues={{
        opportunityId: workFeedItem.opportunity.id,
        roleFitDescription: '',
      }}
      onSubmit={async (changedData, actions, values) => {
        await CandidateOpportunitiesApi.createWorkFeedInterested({
          candidateId: candidate.id,
          body: {
            ...values,
            opportunityId: workFeedItem.opportunity.id,
          },
        });

        return onAfterSubmit();
      }}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name={name} size="large">
            <ModalHeader
              name="PENDO_CONFIRM_INTEREST_OPPORTUNITY_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              Submit profile to Axiom
            </ModalHeader>
            <ModalSection>
              <Grid>
                <GridRow>
                  <GridColumn smallScreenWidth={12} largeScreenWidth={8}>
                    <Gutter bottom="24px">
                      <Well background="blue">
                        <Flashy bold>
                          This lets Axiom know you would like to be considered.
                        </Flashy>{' '}
                        We'll reach out if your profile looks like a good match,
                        usually within 3 business days.
                      </Well>
                    </Gutter>
                    <Gutter bottom="24px">
                      <Textarea
                        label="Tell us why you'd be great for this role."
                        name="roleFitDescription"
                        enableSuggestionsFor="portal-WorkFeedConfirmInterestModal-roleFitDescription"
                      />
                    </Gutter>
                    <Gutter bottom="24px">
                      <Checkbox
                        name="openToLowerEsc"
                        onChange={() => {
                          setShowOpenToLowerEscCompensationPercentage(
                            !showOpenToLowerEscCompensationPercentage
                          );
                        }}
                        displayValue="I would be willing to discuss accepting moderately lower compensation for this specific opportunity if the client's budget is restrictive."
                      />
                    </Gutter>
                    {showOpenToLowerEscCompensationPercentage ? (
                      <Gutter bottom="24px">
                        <Banner name="COMPENSATION_PERCENTAGE_BANNER">
                          <LayoutItem bottomGutter="8px">
                            <CondensedLarge>
                              How much of a reduction in compensation would you
                              be willing to accept for this opportunity? If
                              Axiom moves forward with your candidacy, we will
                              call you to finalize your rate.{' '}
                              <Flashy color="textAlert">*</Flashy>
                            </CondensedLarge>
                          </LayoutItem>
                          <FormGroup name="openToLowerEscCompensationPercentage">
                            <Layout direction="vertical" verticalGutter="8px">
                              {compensationDiscountOptions.map(
                                ({ value, label }) => (
                                  <Radio
                                    key={value}
                                    name="openToLowerEscCompensationPercentage"
                                    option={value}
                                    displayValue={label}
                                  />
                                )
                              )}
                            </Layout>
                          </FormGroup>
                        </Banner>
                      </Gutter>
                    ) : null}
                    <Checkbox
                      name="hasNoConflicts"
                      displayValue="To my knowledge this opportunity does not present any actual or potential conflicts of interest that would prevent me from acting for the client on this matter."
                    />
                    <Gutter bottom="24px" only="smallScreen" />
                  </GridColumn>
                  <GridColumn smallScreenWidth={12} largeScreenWidth={4}>
                    <WorkFeedEOIJobDescription
                      name="WORK_FEED_EOI_JOB_DESCRIPTION"
                      workFeedItem={workFeedItem}
                    />
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                variation="outline"
                name="PENDO_CONFIRM_INTEREST_OPPORTUNITY_CANCEL"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                name="PENDO_CONFIRM_INTEREST_OPPORTUNITY_SUBMIT"
                onClick={fireSubmit}
              >
                {submitButtonText}
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
